import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import api from "../../axiosInstance";
import MentorAvailabilityDialog from "./MentorAvailabilityDialog";
import MentorAvailabilityListDialog from "./MentorAvailabilityListDialog";

const ACCENT = "#17479e";

const whiteSortLabelSx = {
  color: "white !important",
  "&.Mui-active": { color: "white !important" },
  "&:hover": { color: "white !important" },
  "& .MuiTableSortLabel-icon": { color: "white !important" },
};

const columns = [
  { key: "requestId", label: "ID", minWidth: 60, align: "center" },
  { key: "requestedAt", label: "Requested At", minWidth: 170 },
  { key: "status", label: "Status", minWidth: 120, align: "center" },
  { key: "studentName", label: "Student", minWidth: 160 },
  { key: "studentEmail", label: "Student Email", minWidth: 200 },
  { key: "studentPhoneNumber", label: "Student Phone", minWidth: 140 },
  { key: "mentorName", label: "Mentor", minWidth: 160 },
  { key: "mentorEmail", label: "Mentor Email", minWidth: 200 },
  { key: "mentorPhoneNumber", label: "Mentor Phone", minWidth: 140 },
];

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

function MentorStudentNotifies() {
  const [requests, setRequests] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState("requestedAt");
  const [order, setOrder] = useState("desc");

  const [availabilityRow, setAvailabilityRow] = useState(null);
  const [viewAvailabilityRow, setViewAvailabilityRow] = useState(null);

  // Debounce search: wait 500ms after typing before triggering the API
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Reset to first page whenever a filter changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    setIsLoading(true);
    const params = { page, size: rowsPerPage };
    if (debouncedSearch) params.search = debouncedSearch;
    if (statusFilter) params.status = statusFilter;

    api
      .get("/admin/mentors/availability-requests", { params })
      .then((res) => {
        setRequests(res.data?.data?.content ?? []);
        setTotalElements(res.data?.data?.totalElements ?? 0);
      })
      .catch((err) => {
        console.error("Availability requests fetch error:", err);
        setError("Failed to load student notify requests.");
      })
      .finally(() => setIsLoading(false));
  }, [page, rowsPerPage, debouncedSearch, statusFilter]);

  const sortedData = useMemo(() => {
    return [...requests].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [requests, orderBy, order]);

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search by student or mentor name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 260 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="NOTIFIED">Notified</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {requests.length} of {totalElements} requests
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Paper
          sx={{ width: "100%", overflow: "hidden", boxShadow: 2, borderRadius: 3 }}
        >
          <TableContainer sx={{ maxHeight: "calc(100vh - 320px)" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.align}
                      sx={{
                        minWidth: c.minWidth,
                        padding: "10px 12px",
                        backgroundColor: ACCENT,
                        color: "white",
                        fontWeight: 600,
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                      sortDirection={orderBy === c.key ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === c.key}
                        direction={orderBy === c.key ? order : "asc"}
                        onClick={() => handleSort(c.key)}
                        sx={whiteSortLabelSx}
                      >
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    sx={{
                      minWidth: 120,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      right: 0,
                      zIndex: 4,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      sx={{ textAlign: "center", py: 4 }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        No student notify requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedData.map((row) => (
                    <TableRow
                      key={row.requestId}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "rgba(0,0,0,0.02)",
                        },
                      }}
                    >
                      <TableCell align="center" sx={{ padding: "10px 12px" }}>
                        {row.requestId}
                      </TableCell>
                      <TableCell sx={{ padding: "10px 12px" }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.6,
                            px: 1.2,
                            py: 0.4,
                            borderRadius: "8px",
                            background: "#e3f2fd",
                          }}
                        >
                          <AccessTimeIcon sx={{ fontSize: 14, color: ACCENT }} />
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "0.78rem", color: ACCENT }}
                          >
                            {formatDateTime(row.requestedAt)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ padding: "10px 12px" }}>
                        <Chip
                          label={row.status === "NOTIFIED" ? "Notified" : "Pending"}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            color:
                              row.status === "NOTIFIED" ? "#2e7d32" : "#c62828",
                            backgroundColor:
                              row.status === "NOTIFIED" ? "#e8f5e9" : "#ffebee",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ padding: "10px 12px" }}>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                          {row.studentName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: "10px 12px" }}>
                        {row.studentEmail || "—"}
                      </TableCell>
                      <TableCell sx={{ padding: "10px 12px" }}>
                        {row.studentPhoneNumber || "—"}
                      </TableCell>
                      <TableCell sx={{ padding: "10px 12px" }}>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                          {row.mentorName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: "10px 12px" }}>
                        {row.mentorEmail || "—"}
                      </TableCell>
                      <TableCell sx={{ padding: "10px 12px" }}>
                        {row.mentorPhoneNumber || "—"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          padding: "10px 12px",
                          position: "sticky",
                          right: 0,
                          backgroundColor: "#fff",
                          zIndex: 1,
                        }}
                      >
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Set Availability" arrow>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() =>
                                setAvailabilityRow({
                                  mentorId: row.mentorProfileId,
                                  mentorName: row.mentorName,
                                })
                              }
                            >
                              <EventAvailableIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Availabilities" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                setViewAvailabilityRow({
                                  mentorId: row.mentorProfileId,
                                  mentorName: row.mentorName,
                                })
                              }
                            >
                              <EventNoteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 30, 50]}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                paddingLeft: 2,
                paddingRight: 2,
              },
            }}
          />
        </Paper>
      )}

      <MentorAvailabilityDialog
        open={Boolean(availabilityRow)}
        mentorRow={availabilityRow}
        onClose={() => setAvailabilityRow(null)}
        onSuccess={() => setAvailabilityRow(null)}
      />
      <MentorAvailabilityListDialog
        open={Boolean(viewAvailabilityRow)}
        mentorRow={viewAvailabilityRow}
        onClose={() => setViewAvailabilityRow(null)}
      />
    </Box>
  );
}

export default MentorStudentNotifies;
